/**
 * The last thing that runs before bundle bytes leave the renderer, and the
 * first thing that runs on bytes read back in.
 *
 * The field-level allowlist in `settingsBundleGroups.ts` decides what is
 * *collected*; this guard independently re-checks what was *produced*. The two
 * are deliberately redundant: the allowlist cannot see a field that someone
 * adds to an already-exported object, and the guard cannot see a field that is
 * innocently named. Together they cover both storage layers — renderer
 * `localStorage` stores and the main-process MetaInfo records.
 *
 * It throws rather than filtering. A bundle that contains something it should
 * not is a bug in the collector, and silently trimming it would hide that bug
 * until the day the trim itself has a hole.
 */
import {
  allowedGroupIds,
  allowedStoreKeys,
  findDeniedKeyPattern,
  isDeniedStoreKey,
  type SettingsBundleGroup,
} from './settingsBundleManifest';
import { isSafePathSegment } from '../../../types/safePathSegment';
import type { SettingsBundle } from './settingsBundleTypes';

const MAX_DEPTH = 40;

function fail(message: string): never {
  throw new Error(`Settings export blocked — ${message}`);
}

function walk(value: unknown, path: string, depth: number): void {
  if (depth > MAX_DEPTH) {
    fail(`the bundle nests deeper than ${MAX_DEPTH} levels at ${path}`);
  }
  if (Array.isArray(value)) {
    value.forEach((entry, i) => walk(entry, `${path}[${i}]`, depth + 1));
    return;
  }
  if (!value || typeof value !== 'object') return;

  for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
    const here = path ? `${path}.${key}` : key;
    // Order matters: `auth-tokens.dat` also matches the `token` pattern, and
    // the "never travels" wording is the more precise diagnosis.
    if (isDeniedStoreKey(key)) {
      fail(`"${key}" never travels in a settings bundle (found at ${here})`);
    }
    const pattern = findDeniedKeyPattern(key);
    if (pattern) {
      fail(`"${key}" must never be exported (matched "${pattern}" at ${here})`);
    }
    walk(child, here, depth + 1);
  }
}

function assertMetaInfoRecord(record: unknown, index: number): void {
  const at = `metaInfo[${index}]`;
  if (!record || typeof record !== 'object' || Array.isArray(record)) {
    fail(`${at} is not a database meta-info record`);
  }
  const r = record as Record<string, unknown>;
  // `metainfo:list-all` walks every *.json under a connection directory, and
  // the Object-Explorer pins file (`connection.json`) lives there too. It is
  // dropped by `liftToCurrent` today — this check makes that structural rather
  // than incidental, so a future loosening of the lift cannot leak pins.
  if (
    typeof r.connectionId !== 'string' ||
    typeof r.database !== 'string' ||
    !Array.isArray(r.looseRelationships)
  ) {
    fail(`${at} is not a database meta-info record`);
  }
  // These two become path segments in `metaInfoStore.filePath`, and
  // `path.join` collapses `..`. A record that would write outside its own
  // `{metainfoRoot}/{connectionId}/` directory is refused outright — this is
  // an attempt to choose the write target, not a malformed record.
  if (!isSafePathSegment(r.connectionId) || !isSafePathSegment(r.database)) {
    fail(
      `${at} names an unsafe file path (connectionId "${String(r.connectionId)}", ` +
        `database "${String(r.database)}")`,
    );
  }
}

/**
 * Throws if `bundle` contains anything the manifest does not sanction.
 * Returns void on success so callers read as an assertion.
 */
export function assertNoDeniedData(
  bundle: SettingsBundle,
  groups?: readonly SettingsBundleGroup[],
): void {
  const okGroups = allowedGroupIds(groups);
  const okStores = allowedStoreKeys(groups);

  for (const [groupId, payload] of Object.entries(bundle.groups ?? {})) {
    if (!okGroups.has(groupId)) fail(`unknown export group "${groupId}"`);
    if (!payload || typeof payload !== 'object') {
      fail(`group "${groupId}" is not an object`);
    }
    for (const storeKey of Object.keys(payload)) {
      if (isDeniedStoreKey(storeKey)) {
        fail(`"${storeKey}" never travels in a settings bundle (group "${groupId}")`);
      }
      if (!okStores.has(storeKey)) {
        fail(`store "${storeKey}" is not declared by the manifest (group "${groupId}")`);
      }
    }
  }

  if (bundle.metaInfo !== undefined) {
    if (!Array.isArray(bundle.metaInfo)) fail('metaInfo must be an array');
    bundle.metaInfo.forEach(assertMetaInfoRecord);
  }

  walk(bundle.groups ?? {}, 'groups', 0);
  walk(bundle.metaInfo ?? [], 'metaInfo', 0);
}
